import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SpeakerIcon, StopIcon } from '../components/icons'
import { apiJson } from '../lib/api'
import {
  speak,
  speakViaServer,
  stopAllSpeech,
  voicesReady,
  type AudioCapabilities,
} from '../lib/speech'

/**
 * Yaniti sesli oku.
 *
 * ## Yerel ses YOKSA sessiz kalmiyor, SEBEBINI soyluyor
 *
 * Edge/Chrome'daki "natural"/"online" sesler metni MICROSOFT SUNUCULARINA
 * gonderiyor, o yuzden ses listesi `localService === true` ile filtreleniyor.
 * Yerel Turkce ses bulunamazsa iki yanlis secenek var: buluta dusmek (yasak -
 * kullanici bilmeden yanitlarini disariya gonderir) veya sessizce hicbir sey
 * yapmamak (kullanici butona basiyor, hicbir sey olmuyor, sebebini bilmiyor).
 * Ucuncusunu yapiyoruz: soyluyoruz.
 *
 * ## Yetenek sorgusu ONBELLEKLENIYOR
 *
 * Her mesaj bir buton demek; 50 mesajlik bir konusma 50 istek atardi. Sorgu
 * modul seviyesinde bir kez yapiliyor.
 */

let capsPromise: Promise<AudioCapabilities | null> | null = null

function loadCaps(): Promise<AudioCapabilities | null> {
  if (capsPromise === null) {
    capsPromise = apiJson<AudioCapabilities>('/api/audio/capabilities').catch(() => null)
  }
  return capsPromise
}

/** Testler icin: onbellegi sifirlar. */
export function resetCapsCache(): void {
  capsPromise = null
}

export function SpeakButton({ text }: { text: string }) {
  const { t } = useTranslation()
  const [caps, setCaps] = useState<AudioCapabilities | null>(null)
  const [speaking, setSpeaking] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    void loadCaps().then((value) => {
      if (alive) setCaps(value)
    })
    return () => {
      alive = false
    }
  }, [])

  // Bilesen kaldirilirken okuma DURUYOR. Aksi halde kullanici baska bir
  // sohbete gectiginde eski yanit okunmaya devam ediyor.
  useEffect(() => {
    return () => {
      if (speaking) stopAllSpeech()
    }
  }, [speaking])

  // IKI opsiyonel zincir - `caps?.tts.enabled` alan eksikse patliyor
  // (bkz. MicButton'daki ayni not).
  if (!caps?.tts?.enabled || !text.trim()) return null

  async function toggle() {
    setNote(null)
    if (speaking) {
      stopAllSpeech()
      setSpeaking(false)
      return
    }

    // ONCE SUNUCU: kalite tarayicidan bagimsiz ve metin makineden cikmiyor.
    // Sunucu sentezi yoksa (kapali, kurulu dil yok) veya istek basarisizsa
    // asagidaki tarayici okumasina dusuyoruz - kullanici sessizlikle
    // karsilasmamali.
    if (caps!.tts.server) {
      setSpeaking(true)
      try {
        await speakViaServer(text, () => setSpeaking(false))
        return
      } catch {
        setSpeaking(false)
      }
    }

    // Chrome/Edge ses listesini ASENKRON yukluyor: sayfa acildiktan hemen
    // sonraki ilk cagri BOS liste goruyor ve "yerel ses yok" diyor - ikinci
    // tiklamada liste yuklenmis oluyor ve calisiyor. Bu bekleme o yarisi
    // ortadan kaldiriyor; `voicesReady()` liste zaten doluysa ANINDA doner.
    await voicesReady()

    // `require_local_voice` alani eksikse GUVENLI tarafa dusuyoruz: yerel
    // ses zorunlulugunu VARSAYIYORUZ. Tersi, alan kaybolduğunda sessizce
    // bulut seslerine izin vermek olurdu.
    const result = speak(text, caps!.tts.require_local_voice ?? true, () => setSpeaking(false))
    if (!result.ok) {
      setNote(
        result.reason === 'no_local_voice'
          ? t('voice.speak.noLocalVoice')
          : t('voice.speak.unsupported'),
      )
      return
    }
    setSpeaking(true)
  }

  return (
    <>
      <button
        type="button"
        className="copy"
        onClick={toggle}
        aria-pressed={speaking}
        aria-label={speaking ? t('voice.speak.stop') : t('voice.speak.start')}
      >
        {speaking ? <StopIcon /> : <SpeakerIcon />}
      </button>
      {note && (
        <span className="feedback-error" role="status">
          {note}
        </span>
      )}
    </>
  )
}
